// /attendance — Attendance Tracking (PAGES.md §3.61)
//
// Staff attendance overview derived from shift clock-in/clock-out data. Shows
// daily attendance grid, hours worked, and overtime. MANAGER+ can view, edit
// records, and approve/reject late arrivals. Defaults to the current month.

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  DataTable,
  Badge,
  Avatar,
  DatePicker,
  StatCard,
  Skeleton,
  EmptyState,
  Button,
  Input,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import { useAttendance, useAttendanceStats, useUpdateAttendance, useApproveLateArrival } from '@/features/shifts';
import type { AttendanceRecord, AttendanceStatus, LateApprovalDecision, UpdateAttendanceInput } from '@/features/shifts';
import { useAuthStore } from '@/stores/use-auth-store';
import { AssignStaffModal } from './AssignStaffModal';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

type Preset = 'today' | 'yesterday' | 'this_week' | 'last_7' | 'this_month' | 'last_30' | 'custom';

const PRESET_OPTIONS: { value: Preset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom…' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PRESENT', label: 'Present' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'LATE', label: 'Late' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'COMPLETED', label: 'Completed' },
];

const EDIT_STATUS_OPTIONS = STATUS_OPTIONS.filter((o) => o.value !== '');

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function rangeForPreset(p: Preset): { start_date?: string; end_date?: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = toISODate(today);
  switch (p) {
    case 'today':
      return { start_date: end, end_date: end };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const ys = toISODate(y);
      return { start_date: ys, end_date: ys };
    }
    case 'this_week': {
      const d = new Date(today);
      const day = d.getDay(); // 0=Sun
      const monOffset = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + monOffset);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'last_7': {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'this_month': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'last_30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'custom':
      return {};
  }
}

function statusBadgeVariant(status: AttendanceStatus): 'success' | 'danger' | 'warning' | 'info' | 'draft' {
  switch (status) {
    case 'PRESENT':
    case 'COMPLETED': return 'success';
    case 'ABSENT': return 'danger';
    case 'LATE': return 'warning';
    case 'HALF_DAY': return 'info';
    case 'SCHEDULED': return 'draft';
  }
}

function statusLabel(status: AttendanceStatus): string {
  return status === 'HALF_DAY' ? 'Half Day' : status.charAt(0) + status.slice(1).toLowerCase();
}

type Row = AttendanceRecord;

/** A clock time on the store's clock (T14), not the viewer's. */
function formatTime(iso: string | null, timeZone?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone });
}

// ISO (UTC) → datetime-local input value in the manager's local wall-clock time.
function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInputValue(v: string): string | undefined {
  if (!v) return undefined;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? undefined : new Date(t).toISOString();
}

function AttendanceContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const tenantId = useAuthStore((s) => s.profile?.tenant?.id);
  const canManage = (ROLE_LEVELS[role] ?? 0) >= ROLE_LEVELS.MANAGER;
  const [assignOpen, setAssignOpen] = useState(false);

  const page = Number(searchParams.get('page') ?? '1');
  const status = (searchParams.get('status') ?? '') as AttendanceStatus | '';
  const preset = (searchParams.get('preset') ?? 'this_month') as Preset;
  const startDate = searchParams.get('start_date') ?? '';
  const endDate = searchParams.get('end_date') ?? '';

  const range = useMemo(
    () => (preset === 'custom'
      ? { start_date: startDate || undefined, end_date: endDate || undefined }
      : rangeForPreset(preset)),
    [preset, startDate, endDate],
  );

  const { data, isLoading, isFetching } = useAttendance({
    page,
    status: status || undefined,
    start_date: range.start_date,
    end_date: range.end_date,
  });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);
  const updateMutation = useUpdateAttendance();
  const approveMutation = useApproveLateArrival();

  // KPI totals come from a dataset-scoped stats endpoint, not the current page
  // of rows — otherwise they undercount on multi-page data and change as you
  // page through the table (issue #173).
  const { data: stats } = useAttendanceStats({
    status: status || undefined,
    start_date: range.start_date,
    end_date: range.end_date,
  });
  const presentCount = stats?.present ?? 0;
  const absentCount = stats?.absent ?? 0;
  const lateCount = stats?.late ?? 0;

  // Custom-range value for the DatePicker
  const customRange = useMemo<DateRange | undefined>(() => {
    if (!startDate || !endDate) return undefined;
    return { from: new Date(startDate), to: new Date(endDate) };
  }, [startDate, endDate]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function selectPreset(p: Preset) {
    const params = new URLSearchParams(searchParams);
    params.set('preset', p);
    if (p !== 'custom') {
      params.delete('start_date');
      params.delete('end_date');
    }
    params.set('page', '1');
    setSearchParams(params);
  }

  function handleDateChange(r: DateRange) {
    const params = new URLSearchParams(searchParams);
    params.set('preset', 'custom');
    params.set('start_date', toISODate(r.from));
    params.set('end_date', toISODate(r.to));
    params.set('page', '1');
    setSearchParams(params);
  }

  // ─── Edit modal ──────────────────────────────────────────────
  const [editing, setEditing] = useState<Row | null>(null);
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [editStatus, setEditStatus] = useState<AttendanceStatus>('PRESENT');
  const [notes, setNotes] = useState('');

  function openEdit(row: Row) {
    setEditing(row);
    setClockIn(toLocalInputValue(row.clock_in));
    setClockOut(toLocalInputValue(row.clock_out));
    setEditStatus(row.status);
    setNotes(row.notes ?? '');
  }

  const save = useCallback(() => {
    if (!editing) return;
    // Validate the merged pair the same way the backend does: a blank input
    // leaves the stored value untouched, so editing a single field to an
    // inverting value must still be caught inline (issue #175).
    const effIn = clockIn ? new Date(clockIn).getTime() : (editing.clock_in ? new Date(editing.clock_in).getTime() : null);
    const effOut = clockOut ? new Date(clockOut).getTime() : (editing.clock_out ? new Date(editing.clock_out).getTime() : null);
    if (effIn !== null && effOut !== null && effIn >= effOut) {
      toast({ variant: 'warning', title: 'Clock-in must be earlier than clock-out' });
      return;
    }
    const input: UpdateAttendanceInput = { status: editStatus, notes };
    const ci = fromLocalInputValue(clockIn);
    const co = fromLocalInputValue(clockOut);
    if (ci) input.clock_in = ci;
    if (co) input.clock_out = co;
    updateMutation.mutate(
      { id: editing.id, input },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Attendance updated' });
          setEditing(null);
        },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [editing, clockIn, clockOut, editStatus, notes, updateMutation, toast]);

  // Approve / reject the late arrival — keeps the LATE status intact and records
  // who decided and when, instead of overwriting the status (issue #174).
  const decideLate = useCallback((decision: LateApprovalDecision) => {
    if (!editing) return;
    approveMutation.mutate(
      { id: editing.id, input: { decision, notes: notes || undefined } },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: decision === 'APPROVED' ? 'Late arrival approved' : 'Late arrival rejected' });
          setEditing(null);
        },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [editing, notes, approveMutation, toast]);

  const columns: ColumnDef<Row>[] = useMemo(() => {
    const base: ColumnDef<Row>[] = [
      {
        key: 'user_name',
        header: 'Staff',
        render: (_v, row) => (
          <div className="flex items-center gap-3">
            <Avatar name={row.user_name ?? 'Staff'} size="sm" />
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.user_name ?? row.user_id.slice(0, 8)}</p>
          </div>
        ),
      },
      {
        key: 'shift_name',
        header: 'Shift',
        render: (_v, row) => <span className="text-sm text-neutral-700 dark:text-neutral-200">{row.shift_name ?? '—'}</span>,
      },
      {
        key: 'location_name',
        header: 'Location',
        render: (_v, row) => <span className="text-sm text-neutral-700 dark:text-neutral-200">{row.location_name ?? '—'}</span>,
      },
      {
        key: 'clock_in',
        header: 'Clock In',
        render: (_v, row) => <span className="text-sm tabular-nums font-mono">{formatTime(row.clock_in, row.timezone)}</span>,
      },
      {
        key: 'clock_out',
        header: 'Clock Out',
        render: (_v, row) => <span className="text-sm tabular-nums font-mono">{formatTime(row.clock_out, row.timezone)}</span>,
      },
      {
        key: 'hours_worked',
        header: 'Hours',
        numeric: true,
        render: (_v, row) => (
          <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {row.hours_worked != null ? `${row.hours_worked.toFixed(2)} hrs` : '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={statusBadgeVariant(row.status)} size="sm" dot>{statusLabel(row.status)}</Badge>
            {row.is_holiday && <Badge variant="info" size="sm">Holiday</Badge>}
            {row.status === 'LATE' && row.late_approval_status && (
              <Badge variant={row.late_approval_status === 'APPROVED' ? 'success' : 'danger'} size="sm">
                {row.late_approval_status === 'APPROVED' ? 'Approved' : 'Rejected'}
              </Badge>
            )}
          </div>
        ),
      },
    ];
    if (canManage) {
      base.push({
        key: 'id',
        header: '',
        render: (_v, row) => (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <Button
              variant={row.status === 'LATE' ? 'primary' : 'secondary'}
              onClick={() => openEdit(row)}
              className="text-xs px-2.5 py-1"
            >
              {row.status === 'LATE' ? 'Review' : 'Edit'}
            </Button>
          </div>
        ),
      });
    }
    return base;
  }, [canManage]);

  // KPI icons
  const presentIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );
  const lateIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );
  const absentIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-indigo-500 to-blue-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(99,102,241,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Staff Tracking
          </span>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Attendance</h1>
              <p className="mt-1.5 text-sm text-white/90 max-w-xl">
                Track clock-in, clock-out, and hours worked. Late arrivals need manager approval.
              </p>
            </div>
            {canManage && (
              <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)} className="mt-3 shrink-0">
                Assign Staff
              </Button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
              {(data?.meta.total ?? 0).toLocaleString('en-IN')} records
            </span>
            {lateCount > 0 && (
              <span className="rounded-full bg-amber-400/90 text-amber-950 backdrop-blur-sm px-3 py-1 font-bold tabular-nums">
                {lateCount} late — needs review
              </span>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {isLoading ? (
          <>
            <Skeleton variant="rect" height={100} className="rounded-lg" />
            <Skeleton variant="rect" height={100} className="rounded-lg" />
            <Skeleton variant="rect" height={100} className="rounded-lg" />
          </>
        ) : (
          <>
            <StatCard title="Present" value={String(presentCount)} icon={presentIcon} />
            <StatCard title="Absent" value={String(absentCount)} icon={absentIcon} />
            <StatCard title="Late Arrivals" value={String(lateCount)} icon={lateIcon} />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-1.5">
            {PRESET_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => selectPreset(p.value)}
                className={[
                  'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                  preset === p.value
                    ? 'bg-gradient-to-r from-brand-500 to-indigo-500 text-white shadow-sm'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600',
                ].join(' ')}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="w-full lg:w-44">
            <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={(v) => setParam('status', typeof v === 'string' ? v : '')} />
          </div>
        </div>
        {preset === 'custom' && (
          <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-700 max-w-md">
            <DatePicker
              variant="range"
              label="Custom range"
              value={customRange}
              onChange={handleDateChange}
              placeholder="Pick from / to date"
            />
          </div>
        )}
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState variant="page" title="No attendance data" description="Attendance records are created when staff clock in via the POS terminal."
            icon={<svg className="h-20 w-20 text-indigo-300 dark:text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up">
          <DataTable<Row> columns={columns} data={rows} loading={isFetching} totalCount={data?.meta.total} currentPage={page} pageSize={20} onPageChange={(p) => setParam('page', String(p))} stickyHeader />
        </section>
      )}

      {/* Edit / Approve modal */}
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.status === 'LATE' ? 'Review Late Arrival' : 'Edit Attendance'}
        variant="wide"
      >
        {editing && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/60 p-3">
              <Avatar name={editing.user_name ?? 'Staff'} size="sm" />
              <div className="flex-1">
                <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{editing.user_name ?? editing.user_id.slice(0, 8)}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">{editing.shift_name ?? '—'} · {editing.location_name ?? '—'}</p>
              </div>
              <Badge variant={statusBadgeVariant(editing.status)} size="sm" dot>{statusLabel(editing.status)}</Badge>
            </div>
            {editing.status === 'LATE' && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    Late arrival — manager approval
                  </p>
                  {editing.late_approval_status && (
                    <Badge variant={editing.late_approval_status === 'APPROVED' ? 'success' : 'danger'} size="sm" dot>
                      {editing.late_approval_status === 'APPROVED' ? 'Approved' : 'Rejected'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300/90">
                  Approve or reject the lateness. This keeps the record marked LATE and logs your decision —
                  it does not erase the late clock-in.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    onClick={() => decideLate('APPROVED')}
                    loading={approveMutation.isPending}
                    className="text-xs px-3 py-1.5"
                  >
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => decideLate('REJECTED')}
                    loading={approveMutation.isPending}
                    className="text-xs px-3 py-1.5"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">Clock In</span>
                <input
                  type="datetime-local"
                  value={clockIn}
                  onChange={(e) => setClockIn(e.target.value)}
                  className="h-11 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">Clock Out</span>
                <input
                  type="datetime-local"
                  value={clockOut}
                  onChange={(e) => setClockOut(e.target.value)}
                  className="h-11 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none"
                />
              </label>
            </div>

            <Select
              label="Status"
              options={EDIT_STATUS_OPTIONS}
              value={editStatus}
              onChange={(v) => setEditStatus((typeof v === 'string' ? v : 'PRESENT') as AttendanceStatus)}
            />

            <Input label="Notes" value={notes} onChange={(v) => setNotes(v.slice(0, 500))} placeholder="Reason / remarks" />

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="primary" onClick={() => save()} loading={updateMutation.isPending}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>

      <AssignStaffModal open={assignOpen} onClose={() => setAssignOpen(false)} tenantId={tenantId} />
    </div>
  );
}

export function AttendancePage() {
  return (
    <PageContainer title="Attendance" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Attendance' }]}>
      <ErrorBoundary resetKey="attendance">
        <AttendanceContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
